import { useEffect, useState } from 'react';
import { Row, Tab, Tabs, Container, Breadcrumb, Col, Form, Modal, Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Typeahead } from 'react-bootstrap-typeahead';
import { ITicket, ITicketPatchRequest } from '../../commons/interfaces/ticket.interface';
import CustomTable from '../../components/CustomTable';
import tableColumnMetadata from './table.metadata';
import './style.scss';
import Paper from '../../components/Paper';
import ChatbotAPI from '../../services/chatbot.service';
import ManagerAPI from '../../services/manager.service';
import useAuth from '../../hooks/auth.hook';
import useUnit from '../../hooks/unit.hook';
import { ICustomTableCellProps } from '../../components/CustomTableCell/interfaces';
import { ICustomTableFooterProps } from '../../components/CustomTableFooter/interfaces';
import { IChatbotAPIPagination } from '../../commons/interfaces/chatbotAPI.interface';
import { ActionType, ITableColumnMetadata } from '../../components/CustomTable/interfaces';

const chatbotAPI = new ChatbotAPI();
const managerAPI = new ManagerAPI();

type Params = {
  unitId: string;
  startDate: string;
  endDate: string;
};

export default function Ticket() {
  const auth = useAuth();
  const { units } = useUnit();
  const params = useParams<Params>();
  const initialPagination = { page: 1, pageSize: 10, pageCount: 1, totalCount: 1 };
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('WAITING');
  const [tickets, setTickets] = useState<ITicket[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<ITicket | null>(null);
  const [managers, setManagers] = useState<{ id: string; email: string }[]>([]);
  const [selectedManager, setSelectedManager] = useState<{ id: string; email: string } | null>(
    null
  );
  const [pagination, setPagination] = useState<IChatbotAPIPagination>(initialPagination);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const isAdmin = auth.user?.role === 'ADMIN';
  const managerId = auth.user?.id as string;

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await chatbotAPI.listTickets(
        params.unitId as string,
        page,
        statusFilter,
        params.startDate as string,
        params.endDate as string,
        search,
        managerId
      );
      setTickets(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error al obtener los tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [search, page]);

  useEffect(() => {
    setSearch('');
    setTickets([]);
    setPage(1);
    fetchTickets();
  }, [statusFilter, params.unitId]);

  useEffect(() => {
    const fetchAllManagers = async () => {
      if (!showModal) return;

      setLoading(true);
      let allManagers: { id: string; email: string }[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await managerAPI.listManagers(currentPage);
        allManagers = [...allManagers, ...response.data];
        totalPages = response.pagination.pageCount;
        currentPage++;
      } while (currentPage <= totalPages);

      setManagers(allManagers);
      setLoading(false);
    };

    fetchAllManagers();
  }, [showModal]);

  const handleSelectTab = (selectedStatus: string | null) => {
    if (selectedStatus) {
      setStatusFilter(selectedStatus);
    }
  };

  const handleActionClick: ICustomTableCellProps<ITicket>['onClick'] = (item, type) => {
    if (type === 'ASIGNAR') {
      setSelectedTicket(item);
      setShowModal(true);
    } else {
      navigate(`./${item.id}`);
    }
  };

  const handleConfirmAssign = async () => {
    if (!selectedTicket || !selectedManager) {
      alert('Por favor, selecciona un manager antes de asignar.');
      return;
    }

    try {
      const body: ITicketPatchRequest = {
        status: 'EXECUTING',
        manager: { id: selectedManager.id }
      };
      await chatbotAPI.modifyTicket(selectedTicket.id as string, body);

      alert('Ticket asignado con éxito.');
      handleCloseModal();
    } catch (error) {
      console.error('Error asignando el ticket:', error);
      alert('Ocurrió un error al asignar el ticket. Inténtalo nuevamente.');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
    setSelectedManager(null);
    fetchTickets();
  };

  const filteredColumns: ITableColumnMetadata[] = tableColumnMetadata.map((col) => {
    if (col.key !== 'actions') return col;

    return {
      ...col,
      actions: isAdmin
        ? statusFilter === 'EXECUTING'
          ? (['ASIGNAR', 'CHAT'] as ActionType[])
          : (['CHAT'] as ActionType[])
        : (['CHAT'] as ActionType[])
    };
  });

  const handleFooterClick = (value: number) => {
    setPage(page + value);
  };
  const handleKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter') {
      setSearch(ev.currentTarget.value);
      ev.currentTarget.value = '';
    }
  };

  const footerProps: ICustomTableFooterProps = {
    onClick: handleFooterClick,
    pagination,
    loading
  };

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item active>Atención chatbot</Breadcrumb.Item>
        <Breadcrumb.Item active>
          {units.find((unit) => unit.id === params.unitId)?.name}
        </Breadcrumb.Item>
      </Breadcrumb>
      <Paper>
        <Row className="justify-content-space-between mb-4">
          <Col className="d-flex m-auto">
            <span>Gestión de tickets</span>
          </Col>
          <Col xs={6}>
            <Form.Group>
              <Form.Control
                className="rounded-pill"
                type="text"
                placeholder="Busqueda"
                onKeyDown={handleKeyDown}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col>
            <Tabs id="ticket-table" activeKey={statusFilter} onSelect={handleSelectTab}>
              <Tab eventKey="WAITING" title="EN ESPERA"></Tab>
              <Tab eventKey="EXECUTING" title="EN EJECUCIÓN"></Tab>
              <Tab eventKey="CLOSED" title="RESUELTO"></Tab>
            </Tabs>
          </Col>
        </Row>
        <Row>
          <Col>
            <CustomTable<ITicket>
              data={tickets}
              columnsMetadata={filteredColumns}
              onActionClick={handleActionClick}
              footerProps={footerProps}
              loading={loading}
            />
          </Col>
        </Row>
      </Paper>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Asignar Ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTicket && (
            <>
              <p>
                <strong>Código ticket:</strong> {selectedTicket.code}
              </p>
              <Form.Group>
                <Form.Label>Seleccionar manager:</Form.Label>
                <Typeahead
                  id="manager-autocomplete"
                  labelKey="email"
                  options={managers}
                  placeholder="Buscar manager por email..."
                  onChange={(selected: any[]) =>
                    setSelectedManager(selected.length ? selected[0] : null)
                  }
                  selected={selectedManager ? [selectedManager] : []}
                  isLoading={loading}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmAssign}>
            Confirmar Asignación
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
